import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  UploadCloud, Info, ChevronDown, Activity, MapPin,
  Settings, Plane, Gauge, Zap, Fuel, Wind, Navigation,
  ShieldCheck, Trash2, FileText, Layers, Banknote, UserCheck,
  Wrench, RotateCw, Clock
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { message, Select } from "antd";
import {
  AIRCRAFT_TYPES,
  AIRCRAFT_STATES,
  AIRCRAFT_ORIGIN_COUNTRIES,
  AIRCRAFT_REGISTRATION_COUNTRIES,
  AVIONICS,
  EQUIPMENT,
  PROPELLER_TYPES,
  PROPELLER_ROTATIONS,
  ENGINE_STATES,
  ENGINE_COOLINGS
} from "../enums";
import InstrumentLoader from "@/components/InstrumentLoader";
import ConfirmationAlert from "@/components/ConfirmationAlert";
import { AircraftListingDto, AircraftListingDetailsDto } from "@/types";

const GlassCard = ({ children, className = "", title, icon: Icon }: any) => (
  <div className={`bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl flex flex-col h-full ${className}`}>
    {title && (
      <div className="flex items-center gap-2 mb-5 border-b border-slate-800/50 pb-4">
        {Icon && <Icon size={18} className="text-blue-400" />}
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-white">{title}</h3>
      </div>
    )}
    <div className="flex-1">{children}</div>
  </div>
);

const InputField = "w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white placeholder:text-slate-500 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none";
const Label = "text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block ml-1";

const commonSelectProps = {
  variant: "borderless" as const,
  className: "w-full h-11 bg-[#1E293B] border border-[#334155] rounded-xl text-white",
  dropdownStyle: { backgroundColor: '#0F172A', border: '1px solid #334155', color: 'white' },
  styles: {
    selection: { color: 'white', paddingLeft: '8px' },
    input: { color: 'white' }
  }
};

const typesWithoutEngine = ["GLIDER", "PARAGLIDER", "PARAMOTOR", "PARATRIKE", "HANG_GLIDER", "TRIKE", "HOT_AIR_BALLOON", "GAS_BALLOON"];

const AircraftListing = () => {
  const { saveAircraftListing, updateAircraftListing, getAircraftListingDetails } = useAppContext();
  const { aircraftCommissionRate, syncSystemCommissionRates, getDictValue } = useAuth() as any;
  const { params, navigateTo } = useNavigation();

  const isInitialMount = useRef(true);

  const listingFromParams = params?.aircraftListing as (AircraftListingDto & Partial<AircraftListingDetailsDto>) | undefined;
  const isEditMode = !!listingFromParams;

  const [existingMedia, setExistingMedia] = useState<{id: number, url: string}[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [aircraftType, setAircraftType] = useState("SEP");
  const [manufacturer, setManufacturer] = useState("");
  const [builder, setBuilder] = useState("");
  const [model, setModel] = useState("");
  const [location, setLocation] = useState("");
  const [productionYear, setProductionYear] = useState<number | "">("");
  const [registrationDate, setRegistrationDate] = useState("");
  const [originCountry, setOriginCountry] = useState("");
  const [registrationCountry, setRegistrationCountry] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [seats, setSeats] = useState<number | "">("");
  const [condition, setCondition] = useState("");
  const [airframeTime, setAirframeTime] = useState<number | "">("");
  const [usefulLoad, setUsefulLoad] = useState<number | "">("");
  const [mtom, setMtom] = useState<number | "">("");
  const [fuelCapacity, setFuelCapacity] = useState<number | "">("");
  const [fuelType, setFuelType] = useState("");
  const [annualDue, setAnnualDue] = useState("");
  const [avionicsSelected, setAvionicsSelected] = useState<string[]>([]);
  const [avionicsOther, setAvionicsOther] = useState("");
  const [equipmentSelected, setEquipmentSelected] = useState<string[]>([]);
  const [equipmentOther, setEquipmentOther] = useState("");
  const [inspectionInfo, setInspectionInfo] = useState("");
  const [price, setPrice] = useState<number | "">("");
  const [negotiable, setNegotiable] = useState(false);
  const [commissionModel, setCommissionModel] = useState("DEDUCT_FROM_PRICE");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [propellerDetails, setPropellerDetails] = useState<any>({
    type: "", manufacturer: "", serial: "", blades: "", rotation: "", hours: "", notes: ""
  });

  const [noEngines, setNoEngines] = useState(1);
  const [engineDetails, setEngineDetails] = useState<any[]>([{}]);

  const [selectedPrefix, setSelectedPrefix] = useState("");
  const [regSuffix, setRegSuffix] = useState("");

  const [regTouched, setRegTouched] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDraftAlertOpen, setIsDraftAlertOpen] = useState(false);

  const COMMISSION_MODELS = [
    { code: "DEDUCT_FROM_PRICE", value: "Deduce din prețul total (Comision inclus)" },
    { code: "ADD_ON_TOP", value: "Adaugă peste preț (Comision adăugat peste)" }
  ];

  const alertDict = {
    title: getDictValue("aircraft.listing.verification.title"),
    subtitle: isEditMode ? getDictValue("aircraft.listing.verification.title") : getDictValue("aircraft.listing.verification.title"),
    description: getDictValue("aircraft.listing.verification.description"),
    info: isEditMode
      ? [
          "Anunțul va fi ascuns temporar din marketplace în timpul re-verificării.",
          "Procesul de aprobare durează de regulă doar câteva ore.",
          "După aprobare, anunțul va fi republicat automat cu noile date."
        ]
      : [
          "Echipa noastră va valida documentele și datele introduse.",
          "Vei primi o notificare imediat ce anunțul primește aprobarea (Flight Plan Filed).",
          "Odată aprobat, anunțul devine vizibil instant publicului larg."
        ],
    btnCancel: getDictValue("aircraft.listing.button.cancel"),
    btnConfirm: getDictValue("aircraft.listing.button.submit")
  };

  const draftAlertDict = {
    title: "SALVARE ÎN MOD DRAFT",
    subtitle: "Listarea nu va fi publică",
    description: "Anunțul va fi salvat în contul tău fără a fi trimis spre verificare.",
    info: [
      "Acest anunț NU va fi vizibil public în marketplace.",
      "Oricând poți reveni pentru a completa datele lipsă și a-l trimite spre aprobare.",
      "Anunțul va rămâne salvat în siguranță în panoul tău de administrare."
    ],
    btnCancel: "Înapoi la editare",
    btnConfirm: "Confirmă Salvare Draft"
  };

  useEffect(() => {
    if (syncSystemCommissionRates) {
      syncSystemCommissionRates().catch((err: any) => {
        console.error("Nu s-au putut sincroniza comisioanele globale la montarea paginii:", err);
      });
    }
  }, [syncSystemCommissionRates]);

  const isMtomValidValue = mtom === "" || usefulLoad === "" ? true : Number(mtom) > Number(usefulLoad);
  const hasMedia = existingMedia.length > 0 || mediaFiles.length > 0;

  const isRegistrationValidValue = (() => {
    if (!registrationCountry) return true;
    if (!regNumber) return false;

    if (registrationCountry === "OTHER") {
      if (!regTouched && regNumber === "") return true;
      return /^[A-Z0-9-]{3,10}$/.test(regNumber);
    }

    const countryData = AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === registrationCountry);
    if (!countryData || !countryData.prefix) return false;

    const regUpper = regNumber.toUpperCase();
    const foundPrefix = countryData.prefix.find(p => regUpper.startsWith(p.toUpperCase()));

    if (!foundPrefix) return false;

    let suffix = regUpper.substring(foundPrefix.length);
    if (suffix.startsWith("-")) {
      suffix = suffix.substring(1);
    }

    if (!regTouched && suffix === "") return true;

    if (registrationCountry === "US") {
      return /^[A-Z0-9]{1,5}$/.test(suffix);
    }
    return /^[A-Z0-9]{3,5}$/.test(suffix);
  })();

  const isEngineCountLocked = aircraftType === "SEP" || aircraftType === "MEP";

  const computedValues = (() => {
    const inputPrice = Number(price) || 0;
    const rate = Number(aircraftCommissionRate) || 0;

    if (commissionModel === "DEDUCT_FROM_PRICE") {
      const grossValue = inputPrice;
      const commissionValue = grossValue * rate;
      const netValue = grossValue - commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    } else {
      const netValue = inputPrice;
      const commissionValue = netValue * rate;
      const grossValue = netValue + commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    }
  })();

  useEffect(() => {
    if (!listingFromParams) {
      if (aircraftType === "SEP") handleEngineCountChange(1);
      else if (aircraftType === "MEP") handleEngineCountChange(2);
    }
  }, [aircraftType, listingFromParams]);

  // Load Edit Data
  useEffect(() => {
    if (listingFromParams && isInitialMount.current) {
      isInitialMount.current = false;

      // Base listing fields
      setAircraftType(listingFromParams.type || "SEP");
      setManufacturer(listingFromParams.manufacturer || "");
      setModel(listingFromParams.model || "");
      setLocation(listingFromParams.location || "");
      setProductionYear(Number(listingFromParams.productionYear) || "");
      setCondition(listingFromParams.state || "SH");
      setAirframeTime(listingFromParams.flightTime || "");
      setNegotiable(listingFromParams.negotiable || false);
      setPrice(listingFromParams.grossValue || "");

      if (listingFromParams.mediaUrls && listingFromParams.mediaUrls.length > 0) {
        setExistingMedia(listingFromParams.mediaUrls.map((urlStr, idx) => ({ id: idx, url: urlStr })));
      } else if (listingFromParams.media) {
        setExistingMedia(
          listingFromParams.media.map((m: any, idx: number) => ({
            id: idx,
            url: m.mediaData.startsWith("data:") ? m.mediaData : `data:image/jpeg;base64,${m.mediaData}`
          }))
        );
      }

      // Populate details if passed in params or fetch them from backend
      const populateDetails = (details: AircraftListingDetailsDto) => {
        setBuilder(details.builder || "");
        setRegistrationDate(details.registrationDate || "");
        setOriginCountry(details.originCountry || "");
        setRegistrationCountry(details.registrationCountry || "");
        setSerialNumber(details.serialNumber || "");
        setSeats(details.noSeats || "");
        setUsefulLoad(details.usefulLoad || "");
        setMtom(details.mtom || "");
        setFuelCapacity(details.fuelCapacity || "");
        setFuelType(details.fuelType || "");
        setAnnualDue(details.annualDue || "");
        setAvionicsSelected(details.avionics || []);
        setEquipmentSelected(details.equipment || []);
        setInspectionInfo(details.additionalDetails || "");
        setCommissionModel(details.commissionModel || "DEDUCT_FROM_PRICE");
        if (details.commissionModel === "ADD_ON_TOP") {
          setPrice(details.netValue || "");
        }

        if (details.propeller) {
          setPropellerDetails({
            type: details.propeller.type || "",
            manufacturer: details.propeller.manufacturer || "",
            serial: details.propeller.serialNumber || "",
            blades: details.propeller.noBlades || "",
            rotation: details.propeller.rotation || "Clockwise",
            hours: details.propeller.flightTime || "",
            notes: details.propeller.additionalDetails || ""
          });
        }

        if (details.engines && details.engines.length > 0) {
          setNoEngines(details.noEngines || details.engines.length);
          setEngineDetails(
            details.engines.map((e: any) => ({
              type: e.model || "",
              serial: e.serialNumber || "",
              fuel: e.fuelType || "",
              cooling: e.cooling || "",
              configuration: e.configuration || "",
              power: e.power || "",
              cylinders: e.noCilinders || "",
              cylinderCapacity: e.cilinderCapacity || "",
              hours: e.functioningTime || "",
              condition: e.state || "",
              notes: e.additionalDetails || ""
            }))
          );
        }

        if (details.registration) {
          const fullReg = details.registration;
          setRegNumber(fullReg);
          const countryData = AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === details.registrationCountry);
          if (countryData && details.registrationCountry !== "OTHER") {
            const foundPrefix = countryData.prefix.find(p => fullReg.startsWith(`${p}-`));
            if (foundPrefix) {
              setSelectedPrefix(foundPrefix);
              setRegSuffix(fullReg.replace(`${foundPrefix}-`, ""));
            }
          }
        }
      };

      if (listingFromParams.listingId) {
        getAircraftListingDetails(listingFromParams.listingId).then(details => {
          if (details) populateDetails(details);
        });
      } else {
        populateDetails(listingFromParams as AircraftListingDetailsDto);
      }
    }
  }, [listingFromParams, getAircraftListingDetails]);

  const handleCountryChange = (countryCode: string) => {
    setRegistrationCountry(countryCode);
    const countryData = AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === countryCode);
    setRegTouched(false);

    if (countryCode === "OTHER" || !countryData || countryData.prefix.length === 0) {
      setSelectedPrefix("");
      setRegNumber("");
    } else if (countryData.prefix.length === 1) {
      const p = countryData.prefix[0];
      setSelectedPrefix(p);
      setRegNumber(`${p}-${regSuffix}`);
    } else {
      setSelectedPrefix("");
      setRegNumber("");
    }
  };

  const handlePrefixSelect = (prefix: string) => {
    setSelectedPrefix(prefix);
    setRegNumber(`${prefix}-${regSuffix}`);
  };

  const handleSuffixChange = (value: string) => {
    const cleanValue = value.toUpperCase();
    setRegTouched(true);
    setRegSuffix(cleanValue);
    setRegNumber(`${selectedPrefix}-${cleanValue}`);
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setMediaFiles([...mediaFiles, ...Array.from(e.target.files)]);
  };

  const removeExistingImage = (id: number) => {
    setExistingMedia(prev => prev.filter(img => img.id !== id));
  };

  const removeNewFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleEngineCountChange = (value: number) => {
    setNoEngines(value);
    setEngineDetails(Array(value).fill({ condition: "", cooling: "", rotation: "" }));
  };

  const handleEngineChange = (index: number, field: string, value: any) => {
    const copy = [...engineDetails];
    copy[index] = { ...copy[index], [field]: value };
    setEngineDetails(copy);
  };

  const cleanNumber = (val: any) => {
    return val === "" || val === undefined || val === null ? null : Number(val);
  };

  const hasEngine = !typesWithoutEngine.includes(aircraftType);

  const executeSubmit = async (isDraftMode = false) => {
    setIsAlertOpen(false);
    setIsDraftAlertOpen(false);
    setLoading(true);

    try {
      const aircraftListingDto: AircraftListingDto = {
        listingId: isEditMode ? listingFromParams?.listingId : undefined,
        sellerId: isEditMode ? listingFromParams?.sellerId : undefined,
        type: aircraftType,
        manufacturer,
        model,
        location,
        productionYear: productionYear?.toString() || "",
        state: condition,
        flightTime: cleanNumber(airframeTime),
        grossValue: computedValues.grossValue,
        negotiable,
        media: mediaFiles,
        mediaUrls: existingMedia.map(m => m.url),
        draft: isDraftMode,
        active: !isDraftMode,
        verified: false,
        aircraftListingDetailsId: isEditMode ? listingFromParams?.aircraftListingDetailsId : undefined
      };

      // 2. AircraftListingDetailsDto (Technical Specifications)
      const aircraftListingDetailsDto: AircraftListingDetailsDto = {
        builder,
        registrationCountry,
        originCountry,
        registration: regNumber,
        registrationDate,
        serialNumber,
        noSeats: cleanNumber(seats),
        usefulLoad: cleanNumber(usefulLoad),
        mtom: cleanNumber(mtom),
        fuelCapacity: cleanNumber(fuelCapacity),
        fuelType,
        annualDue,
        avionics: [
          ...avionicsSelected.filter(a => a !== "Altele"),
          ...(avionicsSelected.includes("Altele") && avionicsOther ? [avionicsOther] : [])
        ],
        equipment: [
          ...equipmentSelected.filter(e => e !== "Altele"),
          ...(equipmentSelected.includes("Altele") && equipmentOther ? [equipmentOther] : [])
        ],
        additionalDetails: inspectionInfo,
        propeller: hasEngine
          ? {
              type: propellerDetails.type,
              manufacturer: propellerDetails.manufacturer,
              serialNumber: propellerDetails.serial,
              noBlades: cleanNumber(propellerDetails.blades),
              rotation: propellerDetails.rotation,
              flightTime: cleanNumber(propellerDetails.hours),
              additionalDetails: propellerDetails.notes
            }
          : null,
        noEngines,
        engines: hasEngine
          ? engineDetails.map(e => ({
              model: e.type,
              serialNumber: e.serial,
              fuelType: fuelType,
              cooling: e.cooling,
              configuration: e.configuration,
              power: cleanNumber(e.power),
              noCilinders: cleanNumber(e.cylinders),
              cilinderCapacity: cleanNumber(e.cylinderCapacity),
              functioningTime: cleanNumber(e.hours),
              state: e.condition || "",
              additionalDetails: e.notes
            }))
          : null,
        commissionModel,
        commissionRate: computedValues.rate,
        commissionValue: computedValues.commissionValue,
        netValue: computedValues.netValue,
        chargingModel: "BY_COMMISSION"
      };

      if (isEditMode) {
        const success = await updateAircraftListing(aircraftListingDto, aircraftListingDetailsDto);
        if (success) {
          message.success("Listarea a fost actualizată cu succes!");
          navigateTo("dashboard");
        } else {
          message.error("Eroare la actualizarea listării.");
        }
      } else {
        const success = await saveAircraftListing(aircraftListingDto, aircraftListingDetailsDto);
        if (success) {
          message.success("Listarea a fost salvată cu succes!");
          navigateTo("dashboard");
        } else {
          message.error("Eroare la salvarea listării.");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("A apărut o eroare la salvare.");
    } finally {
      setLoading(false);
    }
  };

  const validateAndScroll = () => {
    setIsSubmitted(true);
    setRegTouched(true);

    if (!aircraftType || !manufacturer || !builder || !model || productionYear === "" || !registrationCountry || !serialNumber || !condition || airframeTime === "" || !isMtomValidValue) {
      document.getElementById("specs-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!regNumber || !isRegistrationValidValue) {
      document.getElementById("registration-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!hasMedia) {
      document.getElementById("media-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!location) {
      document.getElementById("gen-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!typesWithoutEngine.includes(aircraftType)) {
      const enginesValid = engineDetails.every(eng => eng && eng.type && eng.serial && eng.condition && eng.hours !== "");
      const propellerValid = propellerDetails && propellerDetails.type && propellerDetails.manufacturer && propellerDetails.serial;

      if (!enginesValid || !propellerValid) {
        document.getElementById("engine-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
        message.error("Vă rugăm să completați toate câmpurile obligatorii.");
        return false;
      }
    }

    if (price === "" || Number(price) <= 0) {
      document.getElementById("price-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    return true;
  };

  const handleDraftClick = () => {
    setIsDraftAlertOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateAndScroll();
    if (isValid) {
      setIsAlertOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={loading} />

      <div className="container max-w-7xl py-12 px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-blue-600 pl-6">
          <div>
            <p className="text-blue-400 font-mono text-sm tracking-[0.2em] mb-1">{getDictValue("aircraft.listing.header")}</p>
            <h1 className="text-5xl font-black text-white tracking-tight italic">
              {getDictValue("aircraft.listing.title")}
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4">
              <p className="text-[10px] text-slate-500 uppercase font-bold"></p>
              <p className="text-orange-500 font-mono text-xs font-bold flex items-center gap-1">
                <Activity size={12} className="animate-pulse" />
              </p>
            </div>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-8">
            <div id="specs-section">
              <GlassCard title={getDictValue("aircraft.listing.specs.title")} icon={Plane}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className={Label}>{getDictValue("aircraft.listing.specs.type.txb")} <span className="text-red-500">*</span></label>
                    <Select
                      {...commonSelectProps}
                      className={`${commonSelectProps.className} ${isSubmitted && !aircraftType ? "border-red-500 ring-1 ring-red-500" : ""}`}
                      placeholder={<span className="text-slate-500"></span>}
                      value={aircraftType || undefined}
                      onChange={val => setAircraftType(val)}
                      options={AIRCRAFT_TYPES.map(cat => ({
                        label: cat.label,
                        options: cat.options.map(opt => ({ label: opt.label, value: opt.code, style: { color: 'white' } }))
                      }))}
                    />
                  </div>
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.specs.manufacturer.txb")} <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !manufacturer ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.specs.manufacturer.placeholder")} value={manufacturer} onChange={e => setManufacturer(e.target.value)} />
                  </div>
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.specs.builder.txb")} <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !builder ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.specs.builder.placeholder")} value={builder} onChange={e => setBuilder(e.target.value)} />
                  </div>
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.specs.model.txb")} <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !model ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.specs.model.placeholder")} value={model} onChange={e => setModel(e.target.value)} />
                  </div>
                  <div id="registration-section">
                    <label className={Label}>{getDictValue("aircraft.listing.specs.registration.txb")} <span className="text-red-500">*</span></label>
                    <div className="flex items-center">
                      {registrationCountry && registrationCountry !== "OTHER" ? (
                        <div className={`flex w-full group relative bg-[#1E293B] border rounded-xl overflow-hidden transition-all ${
                          (regTouched || isSubmitted) && (!regNumber || !isRegistrationValidValue)
                            ? "border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]"
                            : "border-[#334155] focus-within:border-blue-500"
                        }`}>
                          <div className={`min-w-[80px] h-11 flex items-center justify-center border-r bg-white/5 ${
                             (regTouched || isSubmitted) && (!regNumber || !isRegistrationValidValue) ? "border-red-500/50" : "border-[#334155]"
                          }`}>
                            {AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === registrationCountry)?.prefix.length > 1 ? (
                              <Select
                                variant="borderless"
                                placeholder={<span className="text-white font-bold opacity-70">?? -</span>}
                                className="w-full h-full text-white font-bold"
                                dropdownStyle={{ backgroundColor: '#0F172A', border: '1px solid #334155' }}
                                value={selectedPrefix ? `${selectedPrefix}-` : undefined}
                                onChange={(val) => {
                                    handlePrefixSelect(val.replace('-', ''));
                                    setRegTouched(false);
                                }}
                                options={AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === registrationCountry)?.prefix.map(p => ({
                                  label: <span className="text-white font-bold">{p} -</span>,
                                  value: `${p}-`
                                }))}
                              />
                            ) : (
                              <div className="text-white font-bold text-sm tracking-widest px-4">
                                {AIRCRAFT_REGISTRATION_COUNTRIES.find(c => c.code === registrationCountry)?.prefix[0]} -
                              </div>
                            )}
                          </div>
                          <input
                            className="flex-1 h-11 px-4 bg-transparent text-white placeholder:text-slate-500 text-sm outline-none focus:ring-0"
                            placeholder="XXX"
                            value={regSuffix}
                            onBlur={() => setRegSuffix(prev => prev.trim())}
                            onChange={(e) => handleSuffixChange(e.target.value)}
                          />
                        </div>
                      ) : (
                        <input
                          className={`${InputField} ${(regTouched || isSubmitted) && (!regNumber || !isRegistrationValidValue) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                          placeholder={registrationCountry === "OTHER" ? "" : getDictValue("aircraft.listing.specs.registration.placeholder")}
                          value={regNumber}
                          disabled={!registrationCountry}
                          onChange={(e) => {
                            setRegTouched(true);
                            const val = e.target.value.toUpperCase();
                            setRegNumber(val);
                            setRegSuffix(val);
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.specs.serial.txb")} <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !serialNumber ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.specs.serial.placeholder")} value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
                  </div>
                  <div>
                     <label className={Label}>{getDictValue("aircraft.listing.specs.production.year.txb")} <span className="text-red-500">*</span></label>
                     <Select
                       {...commonSelectProps}
                       className={`${commonSelectProps.className} ${isSubmitted && productionYear === "" ? "border-red-500 ring-1 ring-red-500" : ""}`}
                       value={productionYear || undefined}
                       placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.specs.production.year.placeholder")}</span>}
                       onChange={val => setProductionYear(val)}
                       options={Array.from({ length: 70 }, (_, i) => 2026 - i).map(y => ({
                         label: y.toString(),
                         value: y,
                         style: { color: 'white' }
                       }))}
                     />
                  </div>
                  <div><label className={Label}>{getDictValue("aircraft.listing.specs.registration.date.txb")}</label><input type="date" className={InputField} value={registrationDate} onChange={e => setRegistrationDate(e.target.value)} /></div>
                  <div>
                     <label className={Label}>{getDictValue("aircraft.listing.specs.seats.txb")}</label>
                     <Select
                       {...commonSelectProps}
                       placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.specs.seats.placeholder")}</span>}
                       value={seats || undefined}
                       onChange={val => setSeats(val)}
                       options={[1,2,4,6,8,12,20,50].map(s => ({ label: s.toString(), value: s, style: { color: 'white' } }))}
                     />
                  </div>
                  <div><label className={Label}>{getDictValue("aircraft.listing.specs.annual.due.txb")}</label><input type="date" className={InputField} value={annualDue} onChange={e => setAnnualDue(e.target.value)} /></div>
                </div>
              </GlassCard>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { label: getDictValue("aircraft.listing.time.txb"), unit: 'HRS', icon: Gauge, val: airframeTime, set: setAirframeTime, required: true, show: true, error: isSubmitted && airframeTime === "" },
                { label: getDictValue("aircraft.listing.useful.load.txb"), unit: 'KG', icon: Wind, val: usefulLoad, set: setUsefulLoad, show: true, error: !isMtomValidValue },
                { label: getDictValue("aircraft.listing.mtom.txb"), unit: 'KG', icon: Layers, val: mtom, set: setMtom, show: true, error: !isMtomValidValue },
                { label: getDictValue("aircraft.listing.fuel.capacity"), unit: 'L', icon: Fuel, val: fuelCapacity, set: setFuelCapacity, show: !typesWithoutEngine.includes(aircraftType), error: false },
              ]
              .filter(item => item.show)
              .map((item) => (
                <div key={item.label} className={`bg-slate-900/40 border p-5 rounded-3xl transition-all ${
                  item.error ? "border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.1)]" : "border-slate-800 hover:border-blue-500/50"
                }`}>
                  <div className="flex justify-between items-start mb-2">
                    <item.icon size={18} className={item.error ? "text-red-500" : "text-blue-400"} />
                    <span className={`text-[10px] font-black uppercase ${item.error ? "text-red-500" : "text-slate-600"}`}>{item.unit}</span>
                  </div>
                  <input
                    type="number"
                    className="bg-transparent text-xl font-black text-white w-full outline-none"
                    value={item.val}
                    onChange={e => item.set(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  />
                  <p className={`text-[10px] font-bold uppercase mt-1 tracking-widest ${item.error ? "text-red-500" : "text-slate-500"}`}>
                    {item.label} {item.required && <span className="text-red-500">*</span>}
                  </p>
                </div>
              ))}
            </div>

            <GlassCard title={getDictValue("aircraft.listing.systems.title")} icon={Settings}>
                <div className="grid grid-cols-2 gap-10">
                    <div>
                        <label className={Label + " mb-4 border-b border-slate-800 pb-2 flex items-center gap-2"}>
                            <Zap size={12} className="text-orange-500" /> {getDictValue("aircraft.listing.systems.avionics.txb")}
                        </label>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            {AVIONICS.map(opt => {
                                const isActive = avionicsSelected.includes(opt);
                                return (
                                    <div key={opt} className="flex items-center justify-between p-2 rounded-xl hover:bg-white/[0.02] cursor-pointer group"
                                         onClick={() => setAvionicsSelected(prev => isActive ? prev.filter(x => x !== opt) : [...prev, opt])}>
                                        <span className={`text-[9px] font-black uppercase tracking-widest transition-all ${isActive ? 'text-orange-500' : 'text-slate-500'}`}>
                                            {opt}
                                        </span>
                                        <div className="relative flex flex-col items-center justify-center w-7 h-9">
                                            <div className="absolute inset-0 bg-[#0a0f1e] border-x border-slate-800 rounded-sm" />
                                            <div className={`absolute top-0 w-full h-0.5 transition-all duration-300 ${isActive ? 'bg-orange-500 shadow-[0_0_8px_orange]' : 'bg-transparent'}`} />
                                            <div className={`relative w-2 h-4 rounded-full transition-all duration-500 ${isActive ? 'translate-y-[-4px]' : 'translate-y-[4px]'}`}>
                                                <div className={`w-full h-full rounded-full border shadow-md ${isActive ? 'border-orange-400 bg-orange-500' : 'border-slate-600 bg-slate-700'}`} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className={Label + " mb-4 border-b border-slate-800 pb-2 flex items-center gap-2"}>
                            <Layers size={12} className="text-blue-500" /> {getDictValue("aircraft.listing.systems.equipment.txb")}
                        </label>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            {EQUIPMENT.map(opt => {
                                const isActive = equipmentSelected.includes(opt);
                                return (
                                    <div key={opt} className="flex items-center justify-between p-2 rounded-xl hover:bg-white/[0.02] cursor-pointer group"
                                         onClick={() => setEquipmentSelected(prev => isActive ? prev.filter(x => x !== opt) : [...prev, opt])}>
                                        <span className={`text-[9px] font-black uppercase tracking-widest transition-all ${isActive ? 'text-blue-500' : 'text-slate-500'}`}>
                                            {opt}
                                        </span>
                                        <div className="relative flex flex-col items-center justify-center w-7 h-9">
                                            <div className="absolute inset-0 bg-[#0a0f1e] border-x border-slate-800 rounded-sm" />
                                            <div className={`absolute top-0 w-full h-0.5 transition-all duration-300 ${isActive ? 'bg-blue-500 shadow-[0_0_8px_#3b82f6]' : 'bg-transparent'}`} />
                                            <div className={`relative w-2 h-4 rounded-full transition-all duration-500 ${isActive ? 'translate-y-[-4px]' : 'translate-y-[4px]'}`}>
                                                <div className={`w-full h-full rounded-full border shadow-md ${isActive ? 'border-blue-400 bg-blue-500' : 'border-slate-600 bg-slate-700'}`} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </GlassCard>

            <div id="media-section">
              <GlassCard title={getDictValue("aircraft.listing.media.title")} icon={UploadCloud}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <label className={Label}>
                      {getDictValue("aircraft.listing.media.placeholder")} <span className="text-red-500">*</span>
                    </label>
                    {!hasMedia && (
                      <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight">
                        {getDictValue("aircraft.listing.media.mandatory")}
                      </span>
                    )}
                  </div>

                  <label className={`group relative border-2 border-dashed transition-all rounded-3xl p-10 flex flex-col items-center justify-center cursor-pointer ${
                    isSubmitted && !hasMedia
                      ? "border-red-500/80 bg-red-500/5 shadow-[0_0_15px_rgba(239,68,68,0.1)]"
                      : "border-slate-700 hover:border-blue-500/50 bg-slate-900/20"
                  }`}>
                    <UploadCloud className="w-8 h-8 text-blue-500 mb-2" />
                    <p className="text-[10px] text-slate-500 font-bold uppercase">
                      {getDictValue("aircraft.listing.media.placeholder")}
                    </p>
                    <input type="file" multiple accept="image/*" className="hidden" onChange={handleMediaChange} />
                  </label>

                  {(existingMedia.length > 0 || mediaFiles.length > 0) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {existingMedia.map((img) => (
                        <div key={`old-${img.id}`} className="group relative bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden aspect-video shadow-lg">
                          <img src={img.url} alt="Server" className="w-full h-full object-cover opacity-80" />
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-blue-600 text-[7px] font-black rounded text-white tracking-widest uppercase">Server</div>
                          <button
                            type="button"
                            onClick={() => removeExistingImage(img.id)}
                            className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg transition-colors"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}

                      {mediaFiles.map((file, i) => {
                        const previewUrl = URL.createObjectURL(file);
                        return (
                          <div key={`new-${i}`} className="group relative bg-slate-800 border border-orange-500/30 rounded-2xl aspect-video overflow-hidden shadow-lg">
                            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover opacity-60" />
                            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-orange-600 text-[7px] font-black rounded text-white tracking-widest uppercase">Nou</div>
                            <button
                              type="button"
                              onClick={() => removeNewFile(i)}
                              className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg transition-colors"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </GlassCard>
            </div>
          </div>

          <div className="space-y-8">
            <div id="gen-section">
              <GlassCard title={getDictValue("aircraft.listing.gen.title")} icon={MapPin}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className={Label}>{getDictValue("aircraft.listing.gen.registration.country.txb")} <span className="text-red-500">*</span></label>
                    <Select
                      {...commonSelectProps}
                      className={`${commonSelectProps.className} ${isSubmitted && !registrationCountry ? "border-red-500 ring-1 ring-red-500" : ""}`}
                      showSearch
                      placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.gen.registration.country.placeholder")}</span>}
                      optionFilterProp="label"
                      value={registrationCountry || undefined}
                      onChange={handleCountryChange}
                      options={AIRCRAFT_REGISTRATION_COUNTRIES.map(c => ({
                        value: c.code, label: c.label, style: { color: 'white' }
                      }))}
                    />
                  </div>
                  <div><label className={Label}>{getDictValue("aircraft.listing.gen.location.txb")} <span className="text-red-500">*</span></label><input className={`${InputField} ${isSubmitted && !location ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.gen.location.placeholder")} value={location} onChange={e => setLocation(e.target.value)} /></div>
                  <div className="flex flex-col">
                    <label className={Label}>{getDictValue("aircraft.listing.gen.manufacturer.country.txb")}</label>
                    <Select
                      {...commonSelectProps}
                      showSearch
                      placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.gen.manufacturer.country.placeholder")}</span>}
                      optionFilterProp="label"
                      value={originCountry || undefined}
                      onChange={(val) => setOriginCountry(val)}
                      options={AIRCRAFT_ORIGIN_COUNTRIES.map(c => ({
                        value: c.code, label: c.label, style: { color: 'white' }
                      }))}
                    />
                  </div>
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.gen.state.txb")} <span className="text-red-500">*</span></label>
                    <Select
                      {...commonSelectProps}
                      className={`${commonSelectProps.className} ${isSubmitted && !condition ? "border-red-500 ring-1 ring-red-500" : ""}`}
                      placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.gen.state.placeholder")}</span>}
                      value={condition || undefined}
                      onChange={val => setCondition(val)}
                      options={AIRCRAFT_STATES.map(state => ({ label: state.label, value: state.code, style: { color: 'white' } }))}
                    />
                  </div>
                </div>
              </GlassCard>
            </div>

            {!typesWithoutEngine.includes(aircraftType) && (
              <div id="engine-section" className="space-y-8">
                <GlassCard title={getDictValue("aircraft.listing.engine.title")} icon={Zap}>
                  <div className="space-y-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em]">{getDictValue("aircraft.listing.engine.no.engines.txb")}</h4>
                      <div className="w-32">
                        <Select
                          {...commonSelectProps}
                          placeholder={<span className="text-slate-500"></span>}
                          value={noEngines}
                          disabled={isEngineCountLocked}
                          onChange={val => handleEngineCountChange(val)}
                          options={[1,2,3,4].map(n => ({ label: `${n} engines`, value: n, style: { color: 'white' } }))}
                        />
                      </div>
                    </div>

                    {engineDetails.map((engine, i) => (
                      <div key={i} className="p-4 bg-slate-900/50 rounded-2xl border border-slate-800 space-y-4">
                        <p className="text-[10px] font-bold text-slate-500 italic">ENGINE #{i+1} DATA</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.model.txb")} <span className="text-red-500">*</span></label>
                            <input
                              className={`${InputField} ${isSubmitted && !engine.type ? "border-red-500 ring-1 ring-red-500" : ""}`}
                              value={engine.type || ""}
                              placeholder={getDictValue("aircraft.listing.engine.model.placeholder")}
                              onChange={e => handleEngineChange(i, 'type', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.serial.txb")} <span className="text-red-500">*</span></label>
                            <input
                              className={`${InputField} ${isSubmitted && !engine.serial ? "border-red-500 ring-1 ring-red-500" : ""}`}
                              value={engine.serial || ""}
                              placeholder={getDictValue("aircraft.listing.engine.serial.placeholder")}
                              onChange={e => handleEngineChange(i, 'serial', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.fuel.txb")}</label>
                            <input
                              className={InputField}
                              value={fuelType || ""}
                              placeholder={getDictValue("aircraft.listing.engine.fuel.placeholder")}
                              onChange={e => setFuelType(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.power.txb")}</label>
                            <input
                              className={InputField}
                              type="number"
                              placeholder={getDictValue("aircraft.listing.engine.power.placeholder")}
                              value={engine.power || ""}
                              onChange={e => handleEngineChange(i, 'power', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.cooling.txb")}</label>
                            <Select
                              {...commonSelectProps}
                              placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.engine.cooling.placeholder")}</span>}
                              value={engine.cooling || undefined}
                              onChange={val => handleEngineChange(i, 'cooling', val)}
                              options={ENGINE_COOLINGS.map(c => ({ label: c.label, value: c.code, style: { color: 'white' } }))}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.config.txb")}</label>
                            <input
                              className={InputField}
                              value={engine.configuration || ""}
                              placeholder={getDictValue("aircraft.listing.engine.config.placeholder")}
                              onChange={e => handleEngineChange(i, 'configuration', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.no.cilinders.txb")}</label>
                            <input
                              className={InputField}
                              type="number"
                              placeholder={getDictValue("aircraft.listing.engine.no.cilinders.placeholder")}
                              value={engine.cylinders || ""}
                              onChange={e => handleEngineChange(i, 'cylinders', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.capacity.txb")}</label>
                            <input
                              className={InputField}
                              placeholder={getDictValue("aircraft.listing.engine.capacity.placeholder")}
                              value={engine.cylinderCapacity || ""}
                              onChange={e => handleEngineChange(i, 'cylinderCapacity', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.time.txb")} <span className="text-red-500">*</span></label>
                            <input
                              className={`${InputField} ${isSubmitted && (engine.hours === "" || engine.hours === undefined) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                              type="number"
                              placeholder={getDictValue("aircraft.listing.engine.time.placeholder")}
                              value={engine.hours || ""}
                              onChange={e => handleEngineChange(i, 'hours', e.target.value)}
                            />
                          </div>
                          <div>
                            <label className={Label}>{getDictValue("aircraft.listing.engine.state.txb")} <span className="text-red-500">*</span></label>
                            <Select
                              {...commonSelectProps}
                              className={`${commonSelectProps.className} ${isSubmitted && !engine.condition ? "border-red-500 ring-1 ring-red-500" : ""}`}
                              placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.engine.state.placeholder")}</span>}
                              value={engine.condition || undefined}
                              onChange={val => handleEngineChange(i, 'condition', val)}
                              options={ENGINE_STATES.map(s => ({ label: s.label, value: s.code, style: { color: 'white' } }))}
                            />
                          </div>
                        </div>
                        <textarea
                          className={InputField + " h-20 py-2 resize-none"}
                          value={engine.notes || ""}
                          placeholder={getDictValue("aircraft.listing.engine.details.placeholder")}
                          onChange={e => handleEngineChange(i, 'notes', e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </GlassCard>

                <GlassCard title={getDictValue("aircraft.listing.propeller.title")} icon={RotateCw}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <label className={Label}>{getDictValue("aircraft.listing.propeller.type.txb")} <span className="text-red-500">*</span></label>
                      <Select
                        {...commonSelectProps}
                        className={`${commonSelectProps.className} ${isSubmitted && !propellerDetails.type ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.propeller.type.placeholder")}</span>}
                        value={propellerDetails.type || undefined}
                        onChange={val => setPropellerDetails({...propellerDetails, type: val})}
                        options={PROPELLER_TYPES.map(p => ({ label: p.label, value: p.code, style: { color: 'white' } }))}
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className={Label}>{getDictValue("aircraft.listing.propeller.manufacturer.txb")} <span className="text-red-500">*</span></label>
                      <input className={`${InputField} ${isSubmitted && !propellerDetails.manufacturer ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.propeller.manufacturer.placeholder")} value={propellerDetails.manufacturer} onChange={e => setPropellerDetails({...propellerDetails, manufacturer: e.target.value})} />
                    </div>
                    <div>
                      <label className={Label}>{getDictValue("aircraft.listing.propeller.serial.txb")} <span className="text-red-500">*</span></label>
                      <input className={`${InputField} ${isSubmitted && !propellerDetails.serial ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder={getDictValue("aircraft.listing.propeller.serial.placeholder")} value={propellerDetails.serial} onChange={e => setPropellerDetails({...propellerDetails, serial: e.target.value})} />
                    </div>
                    <div><label className={Label}>{getDictValue("aircraft.listing.propeller.no.blades.txb")}</label><input className={InputField} placeholder={getDictValue("aircraft.listing.propeller.no.blades.placeholder")} type="number" value={propellerDetails.blades} onChange={e => setPropellerDetails({...propellerDetails, blades: e.target.value})} /></div>
                    <div>
                      <label className={Label}>{getDictValue("aircraft.listing.propeller.rotation.txb")}</label>
                      <Select
                        {...commonSelectProps}
                        placeholder={<span className="text-slate-500">{getDictValue("aircraft.listing.propeller.rotation.placeholder")}</span>}
                        value={propellerDetails.rotation}
                        onChange={val => setPropellerDetails({...propellerDetails, rotation: val})}
                        options={PROPELLER_ROTATIONS.map(rot => ({ label: rot, value: rot, style: { color: 'white' } }))}
                      />
                    </div>
                    <div><label className={Label}>{getDictValue("aircraft.listing.propeller.time.txb")}</label><input className={InputField} placeholder={getDictValue("aircraft.listing.propeller.time.placeholder")} type="number" value={propellerDetails.hours} onChange={e => setPropellerDetails({...propellerDetails, hours: e.target.value})} /></div>
                    <div className="col-span-2"><label className={Label}>{getDictValue("aircraft.listing.propeller.details.txb")}</label><textarea className={InputField + " h-20 py-2 resize-none"} placeholder={getDictValue("aircraft.listing.propeller.details.placeholder")} value={propellerDetails.notes} onChange={e => setPropellerDetails({...propellerDetails, notes: e.target.value})} /></div>
                  </div>
                </GlassCard>
              </div>
            )}

            <div id="price-section">
              <GlassCard title={getDictValue("aircraft.listing.price.title")} icon={Banknote}>
                <div className="space-y-6">
                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.price.commission.model.txb")} <span className="text-red-500">*</span></label>
                    <Select
                      {...commonSelectProps}
                      value={commissionModel}
                      onChange={val => setCommissionModel(val)}
                      options={COMMISSION_MODELS.map(m => ({
                        label: <span className="text-white">{m.value}</span>,
                        value: m.code
                      }))}
                    />
                  </div>

                  <div className="flex items-center gap-4 bg-slate-900/40 p-4 rounded-2xl border border-slate-800">
                    <div className="flex-1">
                      <label className={Label}>
                        {getDictValue("aircraft.listing.price.gross.txb")} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        className={`bg-transparent text-4xl font-black text-white w-full outline-none placeholder:text-slate-700 ${isSubmitted && (price === "" || Number(price) <= 0) ? "text-red-500" : ""}`}
                        placeholder="000"
                        value={price}
                        onChange={e => setPrice(e.target.value === "" ? "" : parseFloat(e.target.value))}
                      />
                    </div>
                  </div>

                  {Number(price) > 0 && (
                    <div className="bg-blue-600/5 border border-blue-500/10 rounded-2xl p-5 space-y-3 font-mono text-xs">
                      <div className="flex justify-between text-slate-400">
                        <span>{getDictValue("aircraft.listing.price.commission.gross.txb")}:</span>
                        <span className="font-bold text-white text-sm">{computedValues.grossValue.toLocaleString()} EUR</span>
                      </div>
                      <div className="flex justify-between text-slate-400 border-b border-slate-800/60 pb-2">
                        <span>{getDictValue("aircraft.listing.price.commission.value.txb")} ({(computedValues.rate * 100).toFixed(1)}%):</span>
                        <span className="font-bold text-red-400">-{computedValues.commissionValue.toLocaleString()} EUR</span>
                      </div>
                      <div className="flex items-center gap-2 bg-blue-500/10 p-3 rounded-xl text-blue-400 border border-blue-500/20">
                        <Info size={16} className="shrink-0" />
                        <div className="flex-1 flex justify-between items-center">
                          <span className="font-bold text-[10px] uppercase tracking-wider">{getDictValue("aircraft.listing.price.commission.net.txb")}:</span>
                          <span className="text-xl font-black text-emerald-400">{computedValues.netValue.toLocaleString()} EUR</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className={Label}>{getDictValue("aircraft.listing.details.txb")}</label>
                    <textarea
                      className="w-full h-24 p-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white text-sm focus:border-blue-500 outline-none resize-none transition-all"
                      placeholder={getDictValue("aircraft.listing.details.placeholder")}
                      value={inspectionInfo}
                      onChange={e => setInspectionInfo(e.target.value)}
                    />
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>

          <div className="lg:col-span-2 flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-[#0F172A]/90 border border-slate-800 rounded-3xl mt-4 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                <ShieldCheck className="text-blue-400" size={24} />
              </div>
              <div>
                <h4 className="text-white font-black uppercase text-sm italic tracking-widest">{getDictValue("aircraft.listing.verification.title")}</h4>
                <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">{getDictValue("aircraft.listing.verification.description")}</p>
              </div>
            </div>
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full md:w-auto">
              <Button
                variant="ghost"
                type="button"
                onClick={() => navigateTo("dashboard")}
                className="flex-1 md:flex-none h-12 px-6 rounded-xl font-bold uppercase text-xs text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all border border-slate-800/80"
              >
                {getDictValue("aircraft.listing.button.cancel") || "Renunță"}
              </Button>
              <Button
                type="button"
                disabled={loading}
                onClick={handleDraftClick}
                className="flex-1 md:flex-none h-12 px-6 rounded-xl font-black uppercase tracking-wider text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 shadow-lg active:scale-95 transition-all flex items-center gap-2"
              >
                <FileText size={15} className="text-slate-400" />
                Salvează Draft
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 md:flex-none h-12 px-8 rounded-xl font-black uppercase italic tracking-wider text-xs bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-lg shadow-blue-900/40 hover:shadow-blue-600/30 active:scale-95 transition-all"
              >
                {getDictValue("aircraft.listing.button.submit")}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmationAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => executeSubmit(false)}
        dict={alertDict}
      />
      <ConfirmationAlert
        isOpen={isDraftAlertOpen}
        onClose={() => setIsDraftAlertOpen(false)}
        onConfirm={() => executeSubmit(true)}
        dict={draftAlertDict}
      />
    </div>
  );
};

export default AircraftListing;